import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { notifyError } from 'common/compounds/Notification/modules';

import {
    PUBSUB_SAVE_REQUEST,
    PUBSUB_SAVE_SUCCESS,
    PUBSUB_SAVE_ERROR,
    PUBSUB_PAGE_CHANGES_MADE,
    PUBSUB_CONTEXT,
} from './constants';

const BASE_AUTOSAVE_TIMEOUT_LENGTH = 4000;
const MAX_TIMEOUT_DELAY = 30000; // 5 Minutes

const CmsSaveButton = ({ triggerSave, pubSub, ButtonComponent, autosaveEnabled }) => {
    // @todo: break down state. This is debt from refactoring the component
    const [state, setState] = useState({
        isSaving: false,
        hasSaveError: false,
        autosaveFailedAttempts: 0,
    });

    const dispatch = useDispatch();
    const autosaveTimeoutId = useRef();

    useEffect(() => {
        if (pubSub) {
            // eslint-disable-next-line no-use-before-define
            subscribeToSavingEvents();
        }
        return () => {
            // eslint-disable-next-line no-use-before-define
            unsubscribeFromSavingEvents();
        };
    }, [pubSub]);

    // Function called to update local state that save has started
    const onSaveStart = () => {
        setState({
            isSaving: true,
            hasSaveError: false,
        });

        // If user initiates the save, clear out existing timeout
        // eslint-disable-next-line no-use-before-define
        clearAutosaveTimeout();
    };

    // Function called to denote local state that save has completed successfully
    const onSaveSuccess = () => {
        setState({
            isSaving: false,
            hasSaveError: false,
            autosaveFailedAttempts: 0,
        });
    };

    // Function called to denote local state that save failed
    const onSaveError = () => {
        setState({
            isSaving: false,
            hasSaveError: true,
            autosaveFailedAttempts: state.autosaveFailedAttempts + 1,
        });
        dispatch(notifyError("There's been an error saving changes. Please try again"));
    };

    const clearAutosaveTimeout = () => {
        if (autosaveTimeoutId.current) {
            clearTimeout(autosaveTimeoutId.current);
            autosaveTimeoutId.current = null;
        }
    };

    const unsubscribeFromSavingEvents = () => {
        if (!pubSub) {
            return;
        }
        pubSub.unsubscribe(PUBSUB_CONTEXT, PUBSUB_SAVE_REQUEST);
        pubSub.unsubscribe(PUBSUB_CONTEXT, PUBSUB_SAVE_SUCCESS);
        pubSub.unsubscribe(PUBSUB_CONTEXT, PUBSUB_SAVE_ERROR);
    };

    const onSave = () => {
        if (triggerSave && !state.isSaving) {
            const savePromise = triggerSave();

            // If saving returns 'false', its in the middle of saving
            if (savePromise) {
                onSaveStart();

                savePromise
                    // iFrame publishes to the PubSub and returns response on save success
                    // hence, added to catch return stmt; duplicate of PUBSUB_SAVE_SUCCESS event
                    .then(() => {
                        onSaveSuccess();
                    })
                    // iFrame publishes to the PubSub and throws error on save fail
                    // hence, added to catch throw; duplicate of PUBSUB_SAVE_ERROR event
                    .catch(() => {
                        onSaveError();
                    });
            }
        }
    };

    const subscribeToSavingEvents = () => {
        pubSub.subscribe(PUBSUB_SAVE_REQUEST, {
            context: PUBSUB_CONTEXT,
            eventHandler: () => onSaveStart(),
        });
        pubSub.subscribe(PUBSUB_SAVE_SUCCESS, {
            context: PUBSUB_CONTEXT,
            eventHandler: () => onSaveSuccess(),
        });
        pubSub.subscribe(PUBSUB_SAVE_ERROR, {
            context: PUBSUB_CONTEXT,
            eventHandler: () => onSaveError(),
        });
    };

    // When autosave turns off, we cancel the timeout
    useEffect(() => {
        if (!autosaveEnabled) {
            clearAutosaveTimeout();
        }
    }, [autosaveEnabled]);

    // Autosave on changes effect
    useEffect(() => {
        const startAutosaveTimeout = () => {
            const { autosaveFailedAttempts } = state;

            clearAutosaveTimeout();

            if (!autosaveEnabled) {
                return;
            }

            // Exponential increase in timeout for consecutive failed save attempts
            let timeoutLength = autosaveFailedAttempts
                ? Math.floor(BASE_AUTOSAVE_TIMEOUT_LENGTH * Math.pow(autosaveFailedAttempts, 1.4))
                : BASE_AUTOSAVE_TIMEOUT_LENGTH;

            if (timeoutLength > MAX_TIMEOUT_DELAY) {
                timeoutLength = MAX_TIMEOUT_DELAY;
            }

            autosaveTimeoutId.current = setTimeout(() => {
                onSave();
            }, timeoutLength);
        };

        if (pubSub) {
            pubSub.subscribe(PUBSUB_PAGE_CHANGES_MADE, {
                context: PUBSUB_CONTEXT,
                eventHandler: startAutosaveTimeout,
            });
        }
        return () => {
            if (pubSub) {
                pubSub.unsubscribe(PUBSUB_CONTEXT, PUBSUB_PAGE_CHANGES_MADE);
            }
        };
    }, [pubSub, autosaveEnabled]);

    return <ButtonComponent isSaving={state.isSaving} hasSaveError={state.hasSaveError} onClick={onSave} />;
};

CmsSaveButton.propTypes = {
    triggerSave: PropTypes.func,
    pubSub: PropTypes.object,
    ButtonComponent: PropTypes.func.isRequired,
    autosaveEnabled: PropTypes.bool,
};

CmsSaveButton.defaultProps = {
    triggerSave: null,
    pubSub: null,
    autosaveEnabled: false,
};

export default CmsSaveButton;
